import { motion, useReducedMotion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import Markdown from 'react-markdown'
import { Badge, Button, InstallSnippet } from '@mdxui/primitives'
import type { HeroCenterAnnouncementProps } from './types'
import { cn } from '../../lib/cn'
import {
  sectionVariants,
  heroBadgeVariants,
  heroHeadlineVariants,
  heroDescriptionVariants,
  heroButtonsVariants,
  heroMediaVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'
import { CodeWindowHero } from '../code-window'

/**
 * HeroCenterAnnouncement - Centered hero with announcement badge, bottom border, and media below
 *
 * **Background prop:**
 * Use the `background` prop to render a background pattern inside the hero:
 * ```tsx
 * <HeroCenterAnnouncement
 *   title="Build Beautiful Interfaces"
 *   background={<GradientBlur />}
 * />
 * ```
 *
 * **CRITICAL LAYOUT REQUIREMENT:**
 * This component has a full-width `border-b` and uses an internal `container` class.
 * DO NOT wrap this component in a `max-w-*` container - the border will be cut off.
 */
const HeroCenterAnnouncement = ({
  // No fabricated default — the announcement pill is opt-in. When a consumer
  // doesn't pass `announcement`, the `{announcement && (…)}` guard below renders
  // NO pill (rather than leaking demo content like "New Release" onto real sites).
  announcement,
  title = 'Build Beautiful Interfaces with MDXUI',
  subtitle = 'Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.',
  callToAction = 'Get Started',
  callToActionUrl = '#',
  secondaryCallToAction = 'View Docs',
  secondaryCallToActionUrl = '#',
  installSnippet,
  image = '/placeholders/placeholder-1.svg',
  mediaType = 'code',
  codeWindow = {
    code: `import { Hero } from '@mdxui/neo'

export default function Page() {
  return (
    <Hero
      variant="center-announcement"
      title="Build Beautiful Interfaces"
      callToAction="Get Started"
    />
  )
}`,
    language: 'tsx',
  },
  codeTheme = 'auto',
  background,
  behindNavbar = true,
  className,
}: Omit<HeroCenterAnnouncementProps, 'variant'>) => {
  const prefersReducedMotion = useReducedMotion()

  const badgeAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroBadgeVariants
  const headlineAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroHeadlineVariants
  const descAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroDescriptionVariants
  const buttonsAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroButtonsVariants
  const mediaAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroMediaVariants

  return (
    <div
      className={cn(
        'relative overflow-hidden mb-0 md:mb-10',
        behindNavbar && BEHIND_NAVBAR_CLASSES,
        className,
      )}
    >
      {background && <div className="absolute inset-0">{background}</div>}
      <motion.section
        className="relative z-10 overflow-hidden border-b border-border pt-10 md:pt-20 lg:pt-32 pb-2"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container mx-auto">
          <div className="relative pb-10 sm:pb-16">
            <div className="absolute inset-x-0 top-0 -z-10 flex h-full w-full items-center justify-center opacity-100" />
            {announcement && (
              <motion.a
                variants={badgeAnimation}
                href={announcement.url}
                className="group mx-auto flex w-fit items-center gap-2 rounded-full bg-muted/50 py-1 pr-3 pl-1"
              >
                {announcement.badge && <Badge>{announcement.badge}</Badge>}
                {announcement.text && (
                  <p className="hidden items-center gap-1 text-xs text-muted-foreground transition-colors group-hover:text-foreground sm:flex">
                    {announcement.text}
                  </p>
                )}
                <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
              </motion.a>
            )}
            <motion.h1
              variants={headlineAnimation}
              className="mx-auto my-5 max-w-5xl text-balance text-center text-4xl font-semibold md:text-5xl"
            >
              {title}
            </motion.h1>
            <motion.div
              variants={descAnimation}
              className="mx-auto max-w-3xl text-center text-sm text-muted-foreground md:text-base [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm [&_code]:text-foreground"
            >
              <Markdown>{subtitle}</Markdown>
            </motion.div>
            <motion.div
              variants={buttonsAnimation}
              className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:flex-row"
            >
              {installSnippet ? (
                <InstallSnippet
                  package={installSnippet.package}
                  command={installSnippet.command}
                  className="w-full sm:w-auto"
                />
              ) : (
                <>
                  {callToAction && callToActionUrl && (
                    <Button asChild className="w-full sm:w-auto">
                      <a href={callToActionUrl}>
                        {callToAction} <ChevronRight className="size-4" />
                      </a>
                    </Button>
                  )}
                  {secondaryCallToAction && secondaryCallToActionUrl && (
                    <Button asChild variant="outline" className="w-full sm:w-auto">
                      <a href={secondaryCallToActionUrl}>{secondaryCallToAction}</a>
                    </Button>
                  )}
                </>
              )}
            </motion.div>
          </div>
          <motion.div
            variants={mediaAnimation}
            className="translate-y-4 overflow-hidden rounded-t-[calc(var(--radius)*2)] border-x border-t border-border px-1 pt-1 max-w-4xl mx-auto"
          >
            {mediaType === 'code' && codeWindow ? (
              <CodeWindowHero
                code={codeWindow.code}
                language={codeWindow.language}
                filename={codeWindow.filename}
                showHeader={true}
                theme={codeTheme}
                size="full"
                className="h-[450px] md:h-[500px] w-full rounded-t-[calc(var(--radius)*1.5)] rounded-b-none"
              />
            ) : (
              <img
                src={image || '/placeholders/placeholder-1.svg'}
                alt={title}
                className="w-full rounded-t-[calc(var(--radius)*1.5)]"
              />
            )}
          </motion.div>
        </div>
      </motion.section>
    </div>
  )
}

export { HeroCenterAnnouncement }
